import React, { createContext, useContext, useReducer, useRef, useState, useEffect } from 'react'

import { OptimizelyContext } from '@optimizely/react-sdk'
import { useVenueMapState } from '@vividseats/venue-maps-component'
import cloneDeep from 'lodash/cloneDeep'

import { useQuery } from '@/api'
import filterContext from '@/components/pages/production/components/context'
import { SectionDefinition } from '@/components/pages/production/components/context/types'
import ticketDetailsContext from '@/components/pages/production/components/ticket-details/context'
import { JSONMap } from '@/components/shared/map/components/simple-map'
import { DEFAULT_CONTEXT_VALUES, MAP_ENDPOINT } from '@/constants'
import InternationalizationContext from '@/context/internationalization'
import ListingsContext from '@/context/listings'
import { OPTIMIZELY_FEATURE } from '@/optimizely/constants'
import { PageType } from '@/types/app-types'
import { useMobileMediaQuery } from '@/utils'
import { trackFilterEvent } from '@/utils/analytics/ga4'
import { FILTER_ACTION, FILTER_TYPE } from '@/utils/analytics/ga4/types'
import { getCurrencySymbol } from '@/utils/i18n'
import { applyUserInfo } from '@/utils/optimizely/utils'

import LayoutContext from '../layout/context'

import { getMapState, lowestPricedListingInSectionSelector, mapContent } from './selectors'
import type {
    HoveredSectionAction,
    MapContextType,
    MapProviderProps,
    MapState,
    MinPriceAction,
    MouseAction,
    PosAction,
} from './types'
import { ActionType } from './types'

const context = createContext<MapContextType>(DEFAULT_CONTEXT_VALUES)
const { Provider } = context

const initialState: MapState = {
    minPrice: { id: '', value: 0 },
    pos: { x: 0, y: 0 },
    hoveredSectionId: '',
    isMouseDown: false,
    isDragging: false,
    isDoubleClicked: false,
}

type Action = MinPriceAction | PosAction | HoveredSectionAction | MouseAction

const reducer = (mapState: MapState, action: Action) => {
    switch (action.type) {
        case ActionType.SET_HOVERED_SECTION_ID:
            return { ...mapState, hoveredSectionId: action.payload }
        case ActionType.SET_MOUSE_DOWN:
            return { ...mapState, isMouseDown: action.payload }
        case ActionType.SET_DRAGGING:
            return { ...mapState, isDragging: action.payload }
        case ActionType.SET_DOUBLE_CLICK:
            return { ...mapState, isDoubleClicked: action.payload }
        default:
            return mapState
    }
}
const setHoveredSectionId = (dispatch: React.Dispatch<HoveredSectionAction>, payload: string): void => {
    dispatch({ type: ActionType.SET_HOVERED_SECTION_ID, payload })
}
const setIsMouseDown = (dispatch: React.Dispatch<MouseAction>, payload: boolean): void => {
    dispatch({ type: ActionType.SET_MOUSE_DOWN, payload })
}
const setIsDragging = (dispatch: React.Dispatch<MouseAction>, payload: boolean): void => {
    dispatch({ type: ActionType.SET_DRAGGING, payload })
}
const setIsDoubleClicked = (dispatch: React.Dispatch<MouseAction>, payload: boolean): void => {
    dispatch({ type: ActionType.SET_DOUBLE_CLICK, payload })
}

export const MapProvider: React.FC<MapProviderProps> = ({
    children,
    initialFetchDisabled,
    initialProps,
    initialEnabled = true,
}) => {
    const [mapState, dispatch] = useReducer(reducer, initialState)
    const [isMapboxMapVisible, setIsMapboxMapVisible] = useState<boolean>(false)
    const {
        selectors: {
            filteredSectionsWithPriceAndColor,
            getLastSelectedSectionSelector: currentSelectedSection,
            getHighlightedSections: selectedSectionsState,
            getHighlightedRows: selectedRowsState,
        },
        dispatch: {
            onSectionSelectAction,
            onFilterResetAction,
            onRowSelectAction,
            onSetSelectSimilarSnackbarVisibility,
        },
    } = useContext(filterContext)
    const {
        selectors: { composedListings, i18nListingsData },
    } = useContext(ListingsContext)
    const {
        state: { data: layoutData },
    } = useContext(LayoutContext)

    const {
        dispatch: { toggleDetails },
    } = useContext(ticketDetailsContext)

    const {
        selectors: { selectedCurrency },
    } = useContext(InternationalizationContext)

    const { optimizely } = useContext(OptimizelyContext)
    const mapJsonFileName = layoutData?.jsonFileName

    const { data, error, fetching } = useQuery<JSONMap>({
        endpoint: `${MAP_ENDPOINT}${mapJsonFileName}`,
        params: {},
        enabled: !!mapJsonFileName && initialEnabled,
        initialData: initialProps?.data,
        initialFetchDisabled,
    })

    const state = { data, error, fetching: fetching }
    const sections = Object.keys(filteredSectionsWithPriceAndColor)
        .map((key) => ({
            ...filteredSectionsWithPriceAndColor[key],
        }))
        .map((section) => {
            const availableRows = [...(section.availableRows || [])]
            const availableRowsForSection = availableRows.map((seat) => String(seat))
            const price = i18nListingsData?.applyCurrencyConversion ? Math.ceil(section.price) : section.price
            const shownPrice = `${getCurrencySymbol(selectedCurrency)}${price}`

            Object.keys(section.availableRowsData ?? {}).forEach((sectionMbi: string) => {
                if (!section.availableRowsData) return
                const sectionRow = section.availableRowsData[sectionMbi]
                const shownRowPrice = `${getCurrencySymbol(selectedCurrency)}${sectionRow?.minPrice}`
                sectionRow.tooltip = `<p style="color:#fff">From <span style="font-weight:bold;">${shownRowPrice}</span></p>`
            })

            return {
                ...section,
                mbi: section.mbi,
                rows: availableRowsForSection,
                price: price,
                hoverContent: `<p style="color:#fff">From <span style="font-weight:bold;">${shownPrice}</span></p>`,
            } as any
        })

    const onMapResetClickCallback = () => {
        toggleDetails({ listing: null })
    }
    const isMobile = useMobileMediaQuery()
    const selectedSections = selectedSectionsState
    const timer = useRef(0)
    const onSectionClickHandler = (event: SectionDefinition) => {
        trackFilterEvent({
            page_type: PageType.Production,
            filter: FILTER_TYPE.MAP_SECTION,
            action: FILTER_ACTION.CLICKED,
        })
        onSectionSelectAction(event)
        timer.current = window.setTimeout(() => {
            onSetSelectSimilarSnackbarVisibility({
                showSnackbar: true,
            })
        }, 200)
    }

    const onRowClickHandler = (event: { rowId: string }) => {
        trackFilterEvent({
            page_type: PageType.Production,
            filter: FILTER_TYPE.MAP_ROW,
            action: FILTER_ACTION.CLICKED,
        })
        onRowSelectAction(event)
    }

    const {
        map,
        mapElement,
        onMapSectionClickFromListing,
        onMapResetClick,
        setSectionPriceTooltip,
        onMapRevertTicketSelectionZoom,
        mapInitialized,
        mapMetadataLoaded,
    } = useVenueMapState({
        sections,
        selectedSections,
        currentSelectedSection,
        onSectionSelectAction: onSectionClickHandler,
        onFilterResetAction,
        layoutUrl: layoutData?.mapboxLayoutUrl ? `${layoutData?.mapboxLayoutUrl}metadata.json` : undefined,
        onMapResetClickCallback,
        padding: isMobile ? 0 : 16,
        selectedRows: selectedRowsState,
        setSelectedRows: onRowClickHandler,
    })
    useEffect(() => {
        const user = cloneDeep(optimizely?.user)
        if (user && user.attributes) {
            // used by venue_maps_march_24_mobile_test, venue_maps_march_24_desktop_test
            user.attributes.is_mapbox_eligible = !!layoutData?.mapboxLayoutUrl
            applyUserInfo(optimizely, user)
        }
        const desktopMapboxExpEnabled = optimizely?.isFeatureEnabled(OPTIMIZELY_FEATURE.useMapboxMaps)
        const mobileMapboxExpEnabled = optimizely?.isFeatureEnabled(OPTIMIZELY_FEATURE.useMapboxMapMobile)
        const showMapboxMapByExperiment = desktopMapboxExpEnabled || mobileMapboxExpEnabled || false
        setIsMapboxMapVisible(showMapboxMapByExperiment)
    }, [layoutData?.mapboxLayoutUrl, optimizely])
    return (
        <Provider
            value={{
                state,
                selectors: {
                    mapContent: mapContent(state),
                    mapState: getMapState(mapState),
                    lowestPricedListingInSection: lowestPricedListingInSectionSelector(
                        composedListings,
                        mapState.hoveredSectionId,
                    ),
                    venueMapState: {
                        mapElement,
                        onMapSectionClickFromListing,
                        map,
                        onMapResetClick,
                        setSectionPriceTooltip,
                        onMapRevertTicketSelectionZoom,
                        mapMetadataLoaded,
                    },
                    isMapboxMapEnabled: isMapboxMapVisible,
                    mapInitialized: !!mapInitialized,
                },
                dispatch: {
                    setHoveredSectionId: (payload) => setHoveredSectionId(dispatch, payload),
                    setIsMouseDown: (isDown: boolean) => setIsMouseDown(dispatch, isDown),
                    setIsDragging: (isDragging: boolean) => setIsDragging(dispatch, isDragging),
                    setIsDoubleClicked: (isDoubleClicked: boolean) => setIsDoubleClicked(dispatch, isDoubleClicked),
                },
            }}
        >
            {children}
        </Provider>
    )
}

export default context
